import type { StudyAuthoringRoutes } from '../src/study-brief/study-authoring-routes'
import type { StudyBuilderFieldMeaningsRoutes } from '../src/study-brief/study-builder-field-meanings-routes'
import type { StudyBuilderEvaluationRoutes } from '../src/study-brief/study-builder-evaluation-routes'
/**
 * What: The embedded app's typed client for Shopify Worker route groups.
 * Transport / data-flow: Hono's `hc` builds the method, path and query from each route group's type, so a
 *   call the Worker does not serve is a compile error (ADR 2026-09-27 §3.6). Every request still goes through
 *   `shopifyApiFetch`, so App Bridge authentication is unchanged.
 */
import { hc } from 'hono/client'
import type { StudyBuilderOpportunitySeedRoutes } from '../src/study-brief/study-builder-opportunity-seed-route'
import type { StudyRunRoutes } from '../src/run-api-routes'
import type { integrationPropertyRoutes } from '../src/integrations/property-routes'
import { shopifyApiFetch, type ShopifyApiFetchPhase, type ShopifyTokenSource } from './shopifyApiFetch'

type ShopifyApiClientOptions = {
  onPhase?: (phase: ShopifyApiFetchPhase) => void
}

/**
 * Typed client for the converted route groups; add each group here as its routes become chained.
 * Each group keeps its own `hc<Group>()` instantiation instead of one merged Hono app type, so adding a
 * group never slows down type inference for the others (ADR 2026-09-27 §3.6 "slices the client type per
 * route group"). Their route trees are re-exposed under one returned object so every call site still goes
 * through the single `shopifyApiClient` identifier.
 */
export function shopifyApiClient(
  shopify: ShopifyTokenSource,
  fetchImpl?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  options: ShopifyApiClientOptions = {},
) {
  const fetchAdapter = (input: RequestInfo | URL, init?: RequestInit) =>
    shopifyApiFetch(shopify, input, { init, fetchImpl, onPhase: options.onPhase })
  const fieldMeanings = hc<StudyBuilderFieldMeaningsRoutes>('/', { fetch: fetchAdapter })
  const evaluation = hc<StudyBuilderEvaluationRoutes>('/', { fetch: fetchAdapter })
  const studyAuthoring = hc<StudyAuthoringRoutes>('/', { fetch: fetchAdapter })
  const studyBuilderOpportunitySeed = hc<StudyBuilderOpportunitySeedRoutes>('/', { fetch: fetchAdapter })
  const studyRun = hc<StudyRunRoutes>('/', { fetch: fetchAdapter })
  const integration = hc<typeof integrationPropertyRoutes>('/', { fetch: fetchAdapter })
  return {
    api: {
      'study-authoring': studyAuthoring.api['study-authoring'],
      'study-authoring-list': studyAuthoring.api['study-authoring-list'],
      'study-authoring-prepare': studyAuthoring.api['study-authoring-prepare'],
      'study-authoring-status': studyAuthoring.api['study-authoring-status'],
      'study-authoring-run': studyAuthoring.api['study-authoring-run'],
      'study-authoring-results': studyAuthoring.api['study-authoring-results'],
      'study-authoring-delete': studyAuthoring.api['study-authoring-delete'],
      'study-authoring-deletion-status': studyAuthoring.api['study-authoring-deletion-status'],
      'store-intelligence': fieldMeanings.api['store-intelligence'],
      'study-builder': {
        evaluate: evaluation.api['study-builder'].evaluate,
        'seed-from-opportunity': studyBuilderOpportunitySeed.api['study-builder']['seed-from-opportunity'],
        'opportunity-setups': studyBuilderOpportunitySeed.api['study-builder']['opportunity-setups'],
      },
      setups: studyRun.api.setups,
      'run-status': studyRun.api['run-status'],
      integrations: integration.api.integrations,
      'store-knowledge': integration.api['store-knowledge'],
      'research-cards': integration.api['research-cards'],
    },
  }
}
